import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authedFetch, inboxApi, type WpLineBalance } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';

interface Props {
  tenantCode: string;
  chatId: string;
}

type BalanceState = { loading?: boolean; data?: WpLineBalance; error?: unknown };

interface LineItem {
  id: number;
  order_id: number | null;
  phone_number: string | null;
  first_name: string | null;
  last_name: string | null;
  physical_iccid: string | null;
  imsi: string | null;
  activated: boolean;
  esim: boolean;
  is_cancelled: boolean;
  is_frozen: boolean | null;
  ported: boolean;
  manually_ported: boolean;
  validated_by_sms: boolean;
  last_connection: string | null;
  last_mcc: string | null;
  last_mnc_country: string | null;
  cancelled_at: string | null;
}

interface LinesResponse {
  configured: boolean;
  gated?: string;
  reason?: string;
  items?: LineItem[];
  count?: number;
}

export function WpLinesPanel({ tenantCode, chatId }: Props) {
  const { user } = useAuth();
  const [data, setData] = useState<LinesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [balances, setBalances] = useState<Record<number, BalanceState>>({});
  const [showInactive, setShowInactive] = useState(false);

  const checkBalance = async (lineId: number) => {
    setBalances((b) => ({ ...b, [lineId]: { loading: true } }));
    try {
      const res = await inboxApi.wpLineBalance(tenantCode, chatId, lineId);
      setBalances((b) => ({ ...b, [lineId]: { data: res } }));
    } catch (e) {
      setBalances((b) => ({ ...b, [lineId]: { error: e } }));
    }
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const qs = `?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`;
        const res = await authedFetch<LinesResponse>(`/agent/wp/lines${qs}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantCode, chatId, user]);

  if (loading) return <div className="py-1 text-[11px] text-gray-400">Loading…</div>;
  if (error) return <ErrorBanner error={error} label="Error" className="py-1 text-[11px] text-red-600" />;
  if (!data || data.configured === false) return null;
  if (data.gated === 'not_verified') {
    return (
      <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-[11px] text-amber-700">
        Verify the customer (last-4 CC) to view lines.
      </div>
    );
  }

  const items = data.items ?? [];
  const isActive = (l: LineItem) => l.activated && !l.is_cancelled;
  const activeCount = items.filter(isActive).length;
  const inactiveCount = items.length - activeCount;
  const shown = showInactive ? items : items.filter(isActive);
  return (
    <div className="space-y-1.5">
      {inactiveCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500">
            {showInactive ? `All (${items.length})` : `${activeCount} active`}
          </span>
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            className="text-[11px] font-medium text-blue-600 hover:underline"
          >
            {showInactive ? 'Active only' : `Show inactive (${inactiveCount})`}
          </button>
        </div>
      )}
      {shown.length === 0 && (
        <div className="py-1 text-[11px] text-gray-400">
          {items.length === 0 ? 'No lines.' : 'No active lines.'}
        </div>
      )}
      {shown.map((l) => (
        <div key={l.id} className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex items-baseline gap-1.5">
              <span className="text-[12px] font-semibold text-gray-900 flex-shrink-0" dir="ltr">
                {l.phone_number || '(no number)'}
              </span>
              {(l.first_name || l.last_name) && (
                <span className="text-[10px] text-gray-400 truncate">
                  {[l.first_name, l.last_name].filter(Boolean).join(' ')}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1 justify-end">
              <LinePill on={l.activated && !l.is_cancelled} label="active" cls="bg-green-100 text-green-700" />
              <LinePill on={!l.activated && !l.is_cancelled} label="pending" cls="bg-gray-200 text-gray-600" />
              <LinePill on={l.esim} label="eSIM" cls="bg-blue-100 text-blue-700" />
              <LinePill on={l.ported} label="ported" cls="bg-purple-100 text-purple-700" />
              <LinePill on={!!l.is_frozen} label="frozen" cls="bg-cyan-100 text-cyan-700" />
              <LinePill on={l.is_cancelled} label="cancelled" cls="bg-red-100 text-red-700" />
            </div>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-gray-500">
            {l.physical_iccid && <span className="font-mono truncate max-w-[140px]">{l.physical_iccid}</span>}
            {l.last_connection && (
              <span>
                · {fmtDate(l.last_connection)}
                {l.last_mnc_country && <span className="ml-1 uppercase">({l.last_mnc_country})</span>}
              </span>
            )}
            <button
              type="button"
              onClick={() => checkBalance(l.id)}
              disabled={balances[l.id]?.loading}
              className="ml-auto text-[10px] font-medium text-blue-600 hover:underline disabled:opacity-50"
              title="Live balance + data usage (production carrier)"
            >
              {balances[l.id]?.loading ? 'Checking…' : balances[l.id]?.data ? 'Refresh' : 'Balance'}
            </button>
          </div>
          <BalanceView state={balances[l.id]} />
        </div>
      ))}
    </div>
  );
}

function LinePill({ on, label, cls }: { on: boolean; label: string; cls: string }) {
  if (!on) return null;
  return (
    <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide rounded-full ${cls}`}>
      {label}
    </span>
  );
}

function BalanceView({ state }: { state?: BalanceState }) {
  if (!state || state.loading) return null;
  if (state.error) return <ErrorBanner error={state.error} className="mt-0.5 text-[10px] text-red-600" />;
  const b = state.data;
  if (!b) return null;
  if (b.gated === 'not_verified') {
    return <div className="mt-0.5 text-[10px] text-amber-600">Verify the customer to check balance.</div>;
  }
  if (b.ok === false || (b.balance === undefined && !b.prepaid_data_usage?.length)) {
    return <div className="mt-0.5 text-[10px] text-gray-400">Balance unavailable{b.reason ? ` (${b.reason})` : ''}.</div>;
  }
  return (
    <div className="mt-1 pl-2 border-l-2 border-blue-100 text-[11px] text-gray-700">
      {b.balance !== undefined && (
        <div><span className="text-gray-400">balance</span> <span className="font-medium">{String(b.balance)}</span></div>
      )}
      {b.prepaid_data_usage?.map((p, i) => (
        <div key={i} className="text-[10px] text-gray-600">
          {p.package || p.name || `pkg ${i + 1}`}: {p.remaining ?? '?'}{p.total !== undefined ? ` / ${p.total}` : ''} left
          {p.expiration && <span className="text-gray-400"> · exp {String(p.expiration).slice(0, 10)}</span>}
        </div>
      ))}
    </div>
  );
}

function fmtDate(s: string): string {
  return s.length > 10 ? s.slice(0, 16) : s;
}
